import type { ApiFetch } from "./base";
import type { CueStill } from "../live/cueRows";
import { API_BASE, UPLOADS_BASE } from "./base";
import type { StageGeometry } from "../live/liveAnalysis";
import type {
  AnalysisTabViewData,
  DashboardHomeResponse,
  ImproveDetailResponse,
  ImproveOverviewResponse,
  ReportAppResponse,
  SettingsResponse,
  UploadDetailResponse,
  UploadsResponse,
} from "./types";

// The metrics DTOs (HelperClasses.cs) decorate fields with [JsonPropertyName]
// from System.Text.Json, but the API is registered with AddNewtonsoftJson —
// which ignores those attributes and serialises using PascalCase property names.
// Normalise to camelCase so our types line up.
function lcFirst(s: string): string {
  return s.length > 0 ? s[0].toLowerCase() + s.slice(1) : s;
}
function camelize<T = unknown>(input: unknown): T {
  if (Array.isArray(input)) return input.map((v) => camelize(v)) as unknown as T;
  if (input && typeof input === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(input as Record<string, unknown>)) {
      out[lcFirst(k)] = camelize(v);
    }
    return out as T;
  }
  return input as T;
}

// ── Sessions / uploads ────────────────────────────────────────────────────
// EL-App reference: app/views/view.videoList.tsx:305 + consts/consts.ts:21
export function listUploads(
  apiFetch: ApiFetch,
  opts: {
    sportIds?: number[];
    page?: number;
    pageSize?: number;
    // Outcome-score filter (server-side, in SQL on the latest ruleScoreOverall row).
    // noScore selects sessions with no score at all; scoreMin/scoreMax a range. The
    // backend treats them independently, but the UI only ever sends one of the two.
    scoreMin?: number;
    scoreMax?: number;
    noScore?: boolean;
    // Upload-tag filters (server-side, same list+count SQL; multi-entry, values
    // within a filter OR together): tiers = the clip's athleteTier tag (AthleteTiers
    // values); subjectContactIds = the athletes IN the video (the subject tag, not
    // the uploader).
    tiers?: string[];
    subjectContactIds?: number[];
  } = {}
): Promise<UploadsResponse> {
  const sportIds = opts.sportIds ?? [];
  const params: string[] = sportIds.map((id) => `sportIds=${id}`);
  // page/pageSize are honoured by the backend only on the non-sport-filtered path.
  if (opts.page != null && opts.pageSize != null) {
    params.push(`page=${opts.page}`, `pageSize=${opts.pageSize}`);
  }
  if (opts.noScore) {
    params.push("noScore=true");
  } else {
    if (opts.scoreMin != null) params.push(`scoreMin=${opts.scoreMin}`);
    if (opts.scoreMax != null) params.push(`scoreMax=${opts.scoreMax}`);
  }
  for (const t of opts.tiers ?? []) params.push(`tiers=${encodeURIComponent(t)}`);
  for (const id of opts.subjectContactIds ?? []) params.push(`subjectContactIds=${id}`);
  const qs = params.length > 0 ? "?" + params.join("&") : "";
  return apiFetch<UploadsResponse>(`/platform/uploads1${qs}`);
}

// Single-upload status (completion %) — used by SessionDetail's poll so it doesn't fetch the whole
// list every few seconds. Returns a 0- or 1-item UploadsResponse.
export function getUploadStatus(
  apiFetch: ApiFetch,
  sfcId: number
): Promise<UploadsResponse> {
  return apiFetch<UploadsResponse>(`/platform/uploads1/${sfcId}`);
}

// PlatformApi.cs:215 — multipart upload. Mirrors EL-App/utils/FileUploader.tsx:123:
//   Files=<video>, Params=[5 (PREPPED_VID_FILE_SERVICEID), sportId, 0, videoDuration]
// Returns DCR_FileUploadResult { NewRecordIds: [sfcId, serviceOrderId, ...] }.
export interface FileUploadResult {
  newRecordIds?: number[];
  NewRecordIds?: number[];
}
export function uploadFile(
  apiFetch: ApiFetch,
  args: {
    file: File;
    sportId: number;
    videoDurationSec: number;
    /** A trimmed clip's window start (whole seconds); videoDurationSec is then its stop. 0 when not trimmed. */
    startSec?: number;
    keypointsJson?: string;
    analysisMode?: "free" | "premium";
    /** How much of the upload has been sent, 0..1. The form sent is the same with or without it. */
    onProgress?: (fraction: number) => void;
  }
): Promise<FileUploadResult> {
  const form = new FormData();
  form.append("Files", args.file);
  form.append("Params", "5"); // PREPPED_VID_FILE_SERVICEID
  form.append("Params", String(args.sportId));
  form.append("Params", windowStart(args.startSec)); // start
  form.append("Params", String(Math.max(1, Math.round(args.videoDurationSec))));
  // Optional on-device pose pose file (web-pose-extraction): nullable by contract — when absent
  // the form is byte-identical to the pre-feature shape (pinned by the characterization test).
  if (args.keypointsJson) form.append("Keypoints", args.keypointsJson);
  if (args.analysisMode) form.append("AnalysisMode", args.analysisMode);
  return apiFetch<FileUploadResult>(`/platform/uploadNew`, {
    method: "POST",
    body: form,
    ...(args.onProgress ? { onUploadProgress: args.onProgress } : {}),
  });
}

// PlatformApi.cs uploadDraft: the clip, sent as soon as it is dropped, before the technique is chosen. The server
// cuts it and stores its frames meanwhile; confirmDraft then adds what uploadFile adds. Params: serviceId, start,
// stop (the same rounded stop uploadFile sends, so the confirmed split can reuse the frames).
export function uploadDraft(
  apiFetch: ApiFetch,
  args: { file: File; videoDurationSec: number; startSec?: number; onProgress?: (fraction: number) => void }
): Promise<FileUploadResult> {
  const form = new FormData();
  form.append("Files", args.file);
  form.append("Params", "5"); // PREPPED_VID_FILE_SERVICEID
  form.append("Params", windowStart(args.startSec)); // start
  form.append("Params", String(Math.max(1, Math.round(args.videoDurationSec))));
  return apiFetch<FileUploadResult>(`/platform/uploadDraft`, {
    method: "POST",
    body: form,
    ...(args.onProgress ? { onUploadProgress: args.onProgress } : {}),
  });
}

/** A trimmed clip's window start, in the whole seconds the stop is sent in; "0" when the clip is not trimmed. */
const windowStart = (startSec: number | undefined) => String(Math.max(0, Math.round(startSec ?? 0)));

// PlatformApi.cs confirmDraft: what uploadFile sends, without the file, for the draft's upload.
export function confirmDraft(
  apiFetch: ApiFetch,
  args: { uploadId: number; sportId: number; videoDurationSec: number; startSec?: number; keypointsJson?: string; analysisMode?: "free" | "premium" }
): Promise<FileUploadResult> {
  const form = new FormData();
  form.append("UploadId", String(args.uploadId));
  form.append("Params", "5"); // PREPPED_VID_FILE_SERVICEID
  form.append("Params", String(args.sportId));
  form.append("Params", windowStart(args.startSec)); // start
  form.append("Params", String(Math.max(1, Math.round(args.videoDurationSec))));
  if (args.keypointsJson) form.append("Keypoints", args.keypointsJson);
  if (args.analysisMode) form.append("AnalysisMode", args.analysisMode);
  return apiFetch<FileUploadResult>(`/platform/confirmDraft`, { method: "POST", body: form });
}

// PlatformApi.cs:193
export function getUploadById(
  apiFetch: ApiFetch,
  uploadId: number
): Promise<UploadDetailResponse> {
  return apiFetch<UploadDetailResponse>(`/platform/uploadById`, {
    method: "POST",
    body: JSON.stringify({ id: uploadId }),
  });
}

// Controllers/UploadsController.cs mediaStatus: which media exists for an upload — drives
// the Video | 3D tab. Cheap probe (File.Exists server-side).
export interface MediaStatus {
  sfcId: number;
  video: boolean;
  model3d: boolean;
  generating3d?: boolean;
  /** Free-tier analysis artifact (overlay manifest) exists — frame feedback is servable
   * even when no video was rendered (analysis-only free uploads). */
  analysis?: boolean;
  /** Per-position stills from the CURRENT render exist ({sfcId}-{P}-still.png) — the free
   * tab loads those files directly instead of asking the API to redraw a frame per view. */
  stills?: boolean;
  /** Which engine owns this upload's labels (AnalysisEngine). "none" means nothing has claimed
   * it, so the session page may label its frames on-device. */
  analysisEngine?: string;
  /** The uploader asked for premium (at upload, or by upgrading). With `video` false, premium is
   * still on its way. Absent on an API from before the field. */
  premiumRequested?: boolean;
  /** The stage the server's render of this upload is in right now (compose, narration-script,
   * narration-voice, ffmpeg-video); null when none is running. */
  renderStage?: string | null;
  /** Feedback first: the premium coaching is out (getPremiumFeedback), a minute before the video. */
  premiumFeedback?: boolean;
  /** The GPU labels are out as a pose file (getGpuPose): the live view draws those instead of the phone's. */
  gpuPose?: boolean;
}
export function getMediaStatus(apiFetch: ApiFetch, sfcId: number): Promise<MediaStatus> {
  return apiFetch<MediaStatus>(`/uploads/mediaStatus?sfcId=${sfcId}`);
}

/** One coached sentence: its position (P1..P10), frame, text and voice clip (a file on the uploads site). */
export interface PremiumFeedbackItem {
  position: string | null;
  fileId: number;
  overlay?: string;
  text: string;
  audio: string | null;
  seconds: number;
}
/** Controllers/UploadsController.cs premiumFeedback: the coaching, published the moment it is voiced. */
export interface PremiumFeedback {
  sfcId: number;
  generatedAtUtc: string;
  items: PremiumFeedbackItem[];
}
export function getPremiumFeedback(apiFetch: ApiFetch, sfcId: number): Promise<PremiumFeedback> {
  return apiFetch<PremiumFeedback>(`/uploads/premiumFeedback?sfcId=${sfcId}`);
}

/** Controllers/UploadsController.cs freeCoaching: the free tier's written coaching (owner, 2026-10-13), in the premium
 * item shape with no audio — the browser voices it (live/browserVoice). 404 until the free render has written it. */
export interface FreeCoaching extends PremiumFeedback {
  intro?: string | null;
  outro?: string | null;
}
export function getFreeCoaching(apiFetch: ApiFetch, sfcId: number): Promise<FreeCoaching> {
  return apiFetch<FreeCoaching>(`/uploads/freeCoaching?sfcId=${sfcId}`);
}

// Controllers/UploadsController.cs request3dModel: ask the mesh worker to build the 3D model
// for this upload. Idempotent — an existing model or in-flight request both return ok.
export function request3dModel(
  apiFetch: ApiFetch,
  sfcId: number
): Promise<{ sfcId: number; status: "ready" | "generating" }> {
  return apiFetch<{ sfcId: number; status: "ready" | "generating" }>(
    `/uploads/request3dModel?sfcId=${sfcId}`,
    { method: "POST" }
  );
}

// Controllers/LabelsController.cs relabelUpload: ADMIN-only. Archives the upload's client
// keypoints JSON aside (kept as training data) and re-queues its label service orders so the
// AutoLabeller re-labels the existing frames on the GPU. No re-split, no re-upload.
export function relabelUpload(
  apiFetch: ApiFetch,
  uploadId: number
): Promise<{ func_status?: number }> {
  return apiFetch<{ func_status?: number }>(`/labels/relabelUpload`, {
    method: "POST",
    body: JSON.stringify({ uploadId }),
  });
}

// Controllers/PlatformController.cs uploadFrames: an upload's split frames plus the engine that
// owns its labels. Label-independent, so it answers for an upload that has none yet — which is
// exactly when the session page needs it.
export interface UploadFrameItem {
  fileId: number;
  labelled: boolean;
}
export function getUploadFrames(
  apiFetch: ApiFetch,
  uploadId: number
): Promise<{ func_status?: number; values?: UploadFrameItem[]; analysisEngine?: string; captureFps?: number }> {
  return apiFetch(`/platform/uploadFrames?uploadId=${uploadId}`);
}

// Controllers/UploadsController.cs clientPose: the upload's client-pose pose file for the LIVE
// free-analysis view — per-frame keypoints at the split cadence, drawn over the playing clip.
// Proxied as JSON content by the API (the store answers without CORS headers). 404 → null:
// the page keeps the static stills view.
/** GET uploads/liveSeries — the C# analysis pass's live-series artifact (strikes, cadence, knee at
 * strike, per-side joint angles). Rejects with the API's 404 until the pass has produced it; the
 * page then hides the live metric panels rather than compute anything itself. */
export function getLiveSeries(
  apiFetch: ApiFetch,
  sfcId: number
): Promise<import("../live/liveAnalysis").LiveSeries | null> {
  return apiFetch<import("../live/liveAnalysis").LiveSeries>(`/uploads/liveSeries?sfcId=${sfcId}`);
}

/** The premium (GPU) labels as a pose file, in the clientPose format. */
export function getGpuPose(apiFetch: ApiFetch, sfcId: number): Promise<import("../live/liveAnalysis").LivePoseFile> {
  return apiFetch<import("../live/liveAnalysis").LivePoseFile>(`/uploads/gpuPose?sfcId=${sfcId}`);
}

export function getClientPose(
  apiFetch: ApiFetch,
  sfcId: number
): Promise<import("../live/liveAnalysis").LivePoseFile | null> {
  return apiFetch<import("../live/liveAnalysis").LivePoseFile>(
    `/uploads/clientPose?sfcId=${sfcId}`
  ).catch(() => null);
}

// Controllers/UploadsController.cs sourceClip: the ORIGINAL upload clip ({fileId}.mp4), local
// with range support on the box, else a redirect to the store (media needs no CORS).
export function sourceClipUrl(sfcId: number): string {
  return `${API_BASE}/uploads/sourceClip?sfcId=${sfcId}`;
}

// The same original clip sent as a file (eleetz-session-{id}-original.mp4): Session Detail's "Original video" button.
export function sourceClipDownloadUrl(sfcId: number): string {
  return `${sourceClipUrl(sfcId)}&download=true`;
}

// Controllers/PlatformController.cs submitClientLabels: the on-device labelling result for an
// existing upload. Frame-keyed (every frame names its fileId) so the server pairs exactly.
export function submitClientLabels(
  apiFetch: ApiFetch,
  uploadId: number,
  poseFileJson: string,
  opts: { rerun?: boolean } = {}
): Promise<{ func_status?: number }> {
  // rerun: Re-render of a free clip — the server accepts it on a fully labelled upload whose labels came from the phone.
  return apiFetch<{ func_status?: number }>(`/platform/submitClientLabels`, {
    method: "POST",
    body: JSON.stringify({ uploadId, poseFileJson, rerun: !!opts.rerun }),
  });
}
/** One credit pack (BillingController.credits). forSale: this box has a Stripe price for it. */
export type CreditPack = { packKey: string; title: string; credits: number; price: number; currency: string; validityDays: number; forSale: boolean };
/** The signed-in user's premium credits and the packs on sale (GET /billing/credits). */
export type CreditsView = { available: number; nextExpiryUtc: string | null; packs: CreditPack[] };

export function fetchCredits(apiFetch: ApiFetch): Promise<CreditsView> {
  return apiFetch<CreditsView>(`/billing/credits`);
}

/** A gift code as its buyer, the gift page or an admin sees it (BillingController gift endpoints). */
export type GiftView = {
  status: string;
  code: string;
  packKey?: string;
  title?: string;
  credits: number;
  expiresUtc?: string;
  redeemUrl?: string;
};

/** My gift codes: every code the signed-in buyer bought. */
export function fetchMyGifts(apiFetch: ApiFetch): Promise<GiftView[]> {
  return apiFetch<GiftView[]>(`/billing/gifts`);
}

/** Admins: a buyer's gift codes by email. */
/** An account's premium credits as the admin screens show them (BillingController.adminCredits); issued after a grant. */
export type AdminCreditsView = { email: string; userId: number; available: number; nextExpiryUtc: string | null; issued?: number };

/** Admins: an account's premium credits by email; a 404 when no account has it. */
export function fetchAdminCredits(apiFetch: ApiFetch, email: string): Promise<AdminCreditsView> {
  return apiFetch<AdminCreditsView>(`/billing/admin/credits?email=${encodeURIComponent(email)}`);
}

/** Admins: give an account 1 to 50 premium credits; answers its credits after the grant. */
export function grantCredits(apiFetch: ApiFetch, email: string, credits: number): Promise<AdminCreditsView> {
  return apiFetch<AdminCreditsView>(`/billing/admin/grant`, { method: "POST", body: JSON.stringify({ email, credits }) });
}

export function fetchGiftsFor(apiFetch: ApiFetch, email: string): Promise<GiftView[]> {
  return apiFetch<GiftView[]>(`/billing/admin/gifts?email=${encodeURIComponent(email)}`);
}

/** A coach's code or a gift code becomes the user's credits (POST /billing/redeem); 400 = not valid or already used. */
export function redeemCode(apiFetch: ApiFetch, code: string): Promise<unknown> {
  return apiFetch(`/billing/redeem`, { method: "POST", body: JSON.stringify({ code }) });
}

/** One coach's batch (CoachCodesController, admins only). */
export type CoachBatch = { batchId: string; issued: number; redeemed: number };

export function fetchCoachBatches(apiFetch: ApiFetch): Promise<CoachBatch[]> {
  return apiFetch<CoachBatch[]>(`/coachcodes`);
}

/** Mints a batch of single-use codes for a coach, each worth one premium analysis (validDays 0 = never lapse). */
export function mintCoachCodes(apiFetch: ApiFetch, coach: string, count: number, validDays: number): Promise<{ batchId: string; codes: string[] }> {
  return apiFetch(`/coachcodes`, { method: "POST", body: JSON.stringify({ coach, count, validDays }) });
}

/** Opens Stripe Checkout for a pack (POST /billing/checkout); the buyer returns to this upload's page. */
export function startCheckout(apiFetch: ApiFetch, packKey: string, uploadId: number): Promise<{ checkoutUrl: string }> {
  return apiFetch<{ checkoutUrl: string }>(`/billing/checkout`, {
    method: "POST",
    body: JSON.stringify({ packKey, uploadId }),
  });
}


// Controllers/LabelsController.cs upgradeUploadToPro: admins, or anyone paying with a credit (Growth Phase 0). Upgrades a free
// (analysis-only) upload to the premium analysis — the client labels stay as shadow data, the
// label orders go back to the GPU labeller, and the video order is re-queued for the narrated
// render. The uploads list then reports videoOrderPending until the video lands.
export function upgradeUploadToPro(
  apiFetch: ApiFetch,
  uploadId: number
): Promise<{ func_status?: number }> {
  return apiFetch<{ func_status?: number }>(`/labels/upgradeUploadToPro`, {
    method: "POST",
    body: JSON.stringify({ uploadId }),
  });
}

// Controllers/LabelsController.cs rerenderUpload: ADMIN-only. Re-queues ONLY the upload's
// video-generation order so the render sweep re-renders it on its next poll — labels and every
// other order are untouched (that's Re-Label's job).
export function rerenderUpload(
  apiFetch: ApiFetch,
  uploadId: number
): Promise<{ func_status?: number }> {
  return apiFetch<{ func_status?: number }>(`/labels/rerenderUpload`, {
    method: "POST",
    body: JSON.stringify({ uploadId }),
  });
}

// ── Subject tagging (Controllers/PlatformController.cs [101c]–[101e]) ────
// Contact picker rows are PII-minimal by design: id + display name ONLY.
export interface ContactSearchItem {
  contactId: number;
  name: string;
}

// GET platform/contactsSearch — name substring search over ALL contacts
// (NULL-org platform-wide pros/athletes AND user contacts), capped server-side.
export function searchContacts(
  apiFetch: ApiFetch,
  q: string
): Promise<{ func_status?: number; values?: ContactSearchItem[] }> {
  return apiFetch(`/platform/contactsSearch?q=${encodeURIComponent(q)}`);
}

// POST platform/athleteCreate — FIND-OR-CREATE of a platform-wide athlete
// contact by name. existing=true means the name matched an already-known
// athlete and that contact was reused (no duplicate timeline).
export interface AthleteCreateResult {
  func_status?: number;
  contactId: number;
  name?: string;
  existing?: boolean;
}
export async function createAthlete(
  apiFetch: ApiFetch,
  name: string
): Promise<AthleteCreateResult> {
  const resp = await apiFetch<AthleteCreateResult>(
    `/platform/athleteCreate?name=${encodeURIComponent(name)}`,
    { method: "POST" }
  );
  if (resp?.func_status != null && resp.func_status !== 1) {
    throw new Error(`athleteCreate failed (${resp.func_status})`);
  }
  return resp;
}

// POST platform/uploadSetSubject — tag WHO is in the video (owner-only server-side).
// Idempotent: calling again replaces the tag.
export async function setUploadSubject(
  apiFetch: ApiFetch,
  uploadId: number,
  subjectContactId: number
): Promise<void> {
  const resp = await apiFetch<{ func_status?: number }>(
    `/platform/uploadSetSubject?uploadId=${uploadId}&subjectContactId=${subjectContactId}`,
    { method: "POST" }
  );
  if (resp?.func_status != null && resp.func_status !== 1) {
    throw new Error(`uploadSetSubject failed (${resp.func_status})`);
  }
}

// ── Upload tags + rankings (Controllers/PlatformController.cs [101f]–[101k]) ────

// POST platform/uploadSetTier — THIS clip's athlete tier (tier-at-time-of-clip,
// owner-only, idempotent replace). An empty tier clears the tag (= unranked).
export async function setUploadTier(
  apiFetch: ApiFetch,
  uploadId: number,
  tier: string
): Promise<void> {
  const resp = await apiFetch<{ func_status?: number }>(
    `/platform/uploadSetTier?uploadId=${uploadId}&tier=${encodeURIComponent(tier)}`,
    { method: "POST" }
  );
  if (resp?.func_status != null && resp.func_status !== 1) {
    throw new Error(`uploadSetTier failed (${resp.func_status})`);
  }
}

// POST platform/uploadSetEvent — which event the clip is from (owner-only). The call
// sets the FULL event state: empty name clears the tag, and a save without a date
// clears a stale date. eventDate is an ISO calendar date (yyyy-MM-dd).
export async function setUploadEvent(
  apiFetch: ApiFetch,
  uploadId: number,
  eventName: string,
  eventDate: string
): Promise<void> {
  const resp = await apiFetch<{ func_status?: number }>(
    `/platform/uploadSetEvent?uploadId=${uploadId}&eventName=${encodeURIComponent(eventName)}&eventDate=${encodeURIComponent(eventDate)}`,
    { method: "POST" }
  );
  if (resp?.func_status != null && resp.func_status !== 1) {
    throw new Error(`uploadSetEvent failed (${resp.func_status})`);
  }
}

// GET platform/eventsSearch — DISTINCT existing event names (the emergent canonical
// list; blank q returns top suggestions so the picker suggests before typing).
export function searchEvents(
  apiFetch: ApiFetch,
  q: string
): Promise<{ func_status?: number; values?: string[] }> {
  return apiFetch(`/platform/eventsSearch?q=${encodeURIComponent(q)}`);
}

// POST platform/uploadSetEquipment — what the athlete used (owner-only, unstructured
// v1). Per field: blank clears that row; all-blank clears the whole tag.
export async function setUploadEquipment(
  apiFetch: ApiFetch,
  uploadId: number,
  fields: { type: string; brand: string; spec: string }
): Promise<void> {
  const resp = await apiFetch<{ func_status?: number }>(
    `/platform/uploadSetEquipment?uploadId=${uploadId}&type=${encodeURIComponent(fields.type)}&brand=${encodeURIComponent(fields.brand)}&spec=${encodeURIComponent(fields.spec)}`,
    { method: "POST" }
  );
  if (resp?.func_status != null && resp.func_status !== 1) {
    throw new Error(`uploadSetEquipment failed (${resp.func_status})`);
  }
}

// GET platform/equipmentSuggest — DISTINCT existing values for the suggestible
// equipment fields (type|brand). spec is deliberately free text: no suggestions.
export function suggestEquipment(
  apiFetch: ApiFetch,
  field: "type" | "brand",
  q: string
): Promise<{ func_status?: number; values?: string[] }> {
  return apiFetch(`/platform/equipmentSuggest?field=${field}&q=${encodeURIComponent(q)}`);
}

// POST platform/uploadSetUserTags — the FULL set of free-form user tags for an
// upload (owner-only, idempotent replace). Wire format matches storage: the tags
// join with "|" (pipes, not commas — commas appear inside tags). An empty set
// clears the tag. Over-cap sets are REJECTED server-side, never truncated.
export async function setUploadUserTags(
  apiFetch: ApiFetch,
  uploadId: number,
  tags: string[]
): Promise<void> {
  const resp = await apiFetch<{ func_status?: number }>(
    `/platform/uploadSetUserTags?uploadId=${uploadId}&tags=${encodeURIComponent(tags.join("|"))}`,
    { method: "POST" }
  );
  if (resp?.func_status != null && resp.func_status !== 1) {
    throw new Error(`uploadSetUserTags failed (${resp.func_status})`);
  }
}

// GET platform/userTagsSuggest — suggestions from the CALLING USER'S OWN uploads
// only (free-form tags are personal vocabulary — deliberately unlike the global
// eventsSearch/equipmentSuggest pools). Blank q returns the user's tags, capped.
export function suggestUserTags(
  apiFetch: ApiFetch,
  q: string
): Promise<{ func_status?: number; values?: string[] }> {
  return apiFetch(`/platform/userTagsSuggest?q=${encodeURIComponent(q)}`);
}

// GET platform/rankings — the global leaderboard (visible to every authenticated
// user). Each row carries the HYBRID overall (mean of per-technique averages) plus the
// perTechnique breakdown for the client-side technique selector.
export interface TechniqueScore {
  sportId: string;
  clipCount: number;
  averageScore?: number | null;
  latestScore?: number | null;
  lastAnalyzedUtc?: string | null;
}
export interface RankingItem {
  rank: number;
  contactId: number;
  name?: string | null;
  tier?: string | null;
  clipCount: number;
  hybridScore?: number | null;
  latestScore?: number | null;
  lastAnalyzedUtc?: string | null;
  perTechnique: TechniqueScore[];
}
// rankings-pagination: sort = "score" (server default since 2026-08-27 — the hybrid
// ranking order) | "recent" (most recently analyzed athlete first). The rank on each row
// is ALWAYS the score-rank over the full filtered set — recent sort reorders, never
// renumbers. technique switches to that sportId's server-ranked leaderboard (client-side
// re-ranking breaks under paging). Multi-entry filters (2026-08-27, same semantics as
// uploads1): tiers, sports (scorecard sportId strings like "golfSwingDTL" — restricts the
// clips the scores aggregate over), contactIds (specific athletes — narrows who is shown
// while keeping their real rank). Paging follows the uploads1 convention: page+pageSize
// together, totalCount in the response; omitting them returns the full legacy list
// (AthleteGallery relies on that to find one athlete's row).
export type RankingsSort = "recent" | "score";
export function getRankings(
  apiFetch: ApiFetch,
  opts: {
    tiers?: string[]; sort?: RankingsSort; technique?: string;
    sports?: string[]; contactIds?: number[];
    page?: number; pageSize?: number;
  } = {}
): Promise<{ func_status?: number; values?: RankingItem[]; totalCount?: number | null }> {
  const params = new URLSearchParams();
  for (const t of opts.tiers ?? []) params.append("tiers", t);
  for (const s of opts.sports ?? []) params.append("sports", s);
  for (const id of opts.contactIds ?? []) params.append("contactIds", String(id));
  if (opts.sort) params.set("sort", opts.sort);
  if (opts.technique) params.set("technique", opts.technique);
  if (opts.page != null && opts.pageSize != null) {
    params.set("page", String(opts.page));
    params.set("pageSize", String(opts.pageSize));
  }
  const qs = params.toString();
  return apiFetch(`/platform/rankings${qs ? "?" + qs : ""}`);
}

// GET platform/athleteClips — the clip gallery behind a rankings row: every clip in the
// athlete's score history, from the SAME scorecard scan as the rankings (the two views can
// never disagree). Public-within-the-app v1, exactly like rankings; per-upload visibility
// metadata + premium privacy gating is the named successor. Newest-first, capped
// server-side. tier is the tier-at-clip (the upload's own tag), not the current tier.
export interface AthleteClipItem {
  uploadId: number;
  sportId?: string | null;
  // The blended finalScore (the score ruler everywhere since #422) when the card carries
  // one; older cards only have the rule-only roll-up below. Display finalScore ?? rule.
  finalScore?: number | null;
  ruleOverallScore?: number | null;
  analyzedAtUtc?: string | null;
  // The clip's first split frame ({previewFileId}.png on the uploads store) — the card
  // preview. null = no frames resolvable; the card falls straight to the emoji.
  previewFileId?: number | null;
  tier?: string | null;
  eventName?: string | null;
  eventDate?: string | null;
}
export function getAthleteClips(
  apiFetch: ApiFetch,
  contactId: number
): Promise<{ func_status?: number; values?: AthleteClipItem[] }> {
  return apiFetch(`/platform/athleteClips?contactId=${contactId}`);
}

// A clip frame on the uploads store: {fileId}.png — the split-frame images the renderer's
// prefetcher reads (CreateGenericFeedbackVideo.cs GetByteArrayAsync
// "https://uploads.eleetz.com/{fileId}.png", pinned by CreateGenericFeedbackVideoTests).
// Gallery previews use the clip's previewFileId (its FIRST frame). Note {sfcId}.png is NOT
// a frame of the clip (it can be an unrelated asset) — never build card previews from it.
export function frameThumbUrl(fileId: number): string {
  return `${UPLOADS_BASE}${fileId}.png`;
}

// POST platform/uploadsRequestRescore (rescore-triggers) — queue a re-scoring for a LIST
// of uploads (per-row button sends one id; the checkbox bulk action sends many). Appends a
// rescoreRequested metadata row per upload (append semantics — history, never overwrite);
// the renderer's backfill loop regenerates anything whose output predates its latest
// request, and the fresh render appends new versioned rule-score rows. Owner-only for
// EVERY id: one foreign id rejects the whole request with nothing written.
export async function requestRescore(
  apiFetch: ApiFetch,
  uploadIds: number[]
): Promise<void> {
  const qs = uploadIds.map((id) => `uploadIds=${id}`).join("&");
  const resp = await apiFetch<{ func_status?: number }>(
    `/platform/uploadsRequestRescore?${qs}`,
    { method: "POST" }
  );
  if (resp?.func_status != null && resp.func_status !== 1) {
    throw new Error(`uploadsRequestRescore failed (${resp.func_status})`);
  }
}

// Controllers/UploadsController.cs:263
export function deleteUpload(
  apiFetch: ApiFetch,
  serviceFulfilmentConfigId: number
): Promise<void> {
  return apiFetch<void>(
    `/uploads/deleteUpload?serviceFulfilmentConfigId=${serviceFulfilmentConfigId}`,
    { method: "DELETE" }
  );
}

// Controllers/UploadsController.cs:getPPositionFrames — JSON list of P1..P10 frame file ids.
export interface PPositionFrame {
  pPositionId: number;
  label: string;
  fileId: number | null;
}
export interface PPositionFramesResponse {
  sfcId: number;
  positions: PPositionFrame[];
}
export function getPPositionFrames(
  apiFetch: ApiFetch,
  sfcId: number
): Promise<PPositionFramesResponse> {
  return apiFetch<PPositionFramesResponse>(
    `/uploads/getPPositionFrames?sfcId=${sfcId}`,
  );
}

// Controllers/UploadsController.cs freeFeedback — the Feedback (Free) tab's ONE request: which
// P-position stills exist, as URLs (relative to the API base). The stills are the cache: the first
// request for an older clip runs the analysis once and writes them; after that it is file reads.
export interface FreeFeedbackPosition {
  label: string;
  stillUrl: string;
}
export interface FreeFeedbackResponse {
  sfcId: number;
  positions: FreeFeedbackPosition[];
  /** The renderer's placement + crop for these stills (sfc{id}_stage.json), so the live playback
   * can frame the clip identically. Null/absent for uploads rendered before it was published. */
  stage?: StageGeometry | null;
  /** The upload's split window in source-video seconds: the pose file frames pair with the split
   * frames sampled every 1/fps from startSec, so playback pairs frame i with startSec + i/fps and
   * loops inside the analysed span. Null when unknown. `fps` is the splitter's stored capture
   * cadence for this upload (10 for golf, the native rate for movement sports); absent on an
   * older API = the legacy 10. */
  split?: { startSec: number; stopSec: number; fps?: number } | null;
  /** One still per cue ({sfcId}-{P}-{cue}-still.png), listed from disk: the stage shows each cue on its
   * own, like the Pro video. Empty/absent for uploads rendered before cue stills existed. */
  cueStills?: CueStill[];
}
export function getFreeFeedback(apiFetch: ApiFetch, sfcId: number): Promise<FreeFeedbackResponse> {
  return apiFetch<FreeFeedbackResponse>(`/uploads/freeFeedback?sfcId=${sfcId}`);
}

// Per-frame overlay outputs — emitted by EL-Golf/BaseSetFinder.WriteOverlayOutputs
// alongside the rendered video. The API bundles every fileId × overlay class
// for an upload into one response so the front-end can load once and filter.

export interface OverlayShape {
  shapeType: string;
  overlayKey: string;
  key: string;
  zIndex: number;
  color: string;
  style: string;
  strokeWidth: number;
  shapeKeyValues: Record<string, string>;
}

export interface OverlayPayload {
  fileId: number;
  overlay: string;
  renderedAt: string;
  /** The P-position the RENDERER drew this overlay for. Authoritative: the positions endpoint
   * recomputes its own frame ids and can disagree with the render (a cadence-windowed detector
   * landing a few frames away), and matching on fileId alone then drops the whole phase. Absent on
   * bundles rendered before the stamp existed — callers fall back to the fileId map. */
  position?: string;
  shapes: OverlayShape[];
}

export interface OverlayBundleFrame {
  fileId: number;
  overlays: OverlayPayload[];
}

// One overlay scene's start second in the overview feedback video (renders since
// scene-time stamping landed; older renders simply don't carry them).
export interface SceneStart {
  fileId: number;
  overlay: string;
  startSeconds: number;
}

export interface OverlayBundle {
  sfcId: number;
  frames: OverlayBundleFrame[];
  feedbackVideos?: Record<string, string>;
  sceneStartSeconds?: SceneStart[];
}

/** The exact second a coaching row's scene appears in the overview video: the row's own
 * overlay when stamped, else the earliest scene on its frame. Null = no scene times for
 * that frame (old render) — the caller falls back to its positional guess. */
export function sceneStartFor(
  scenes: SceneStart[] | undefined,
  fileId: number,
  overlay?: string | null,
): number | null {
  const forFile = (scenes ?? []).filter((s) => s.fileId === fileId);
  if (forFile.length === 0) return null;
  if (overlay) {
    const hit = forFile.find((s) => s.overlay === overlay);
    if (hit) return hit.startSeconds;
  }
  return Math.min(...forFile.map((s) => s.startSeconds));
}

export function getOverlayBundle(
  apiFetch: ApiFetch,
  sfcId: number,
): Promise<OverlayBundle> {
  return apiFetch<OverlayBundle>(`/uploads/getOverlayBundle?sfcId=${sfcId}`);
}

// ── Analysis (3-tab) — mirrors EL-App/app/views/view.videoAnalysis.tsx:238–382
export function getAnalysisData(
  apiFetch: ApiFetch,
  uploadId: number
): Promise<{ metaDataList?: unknown[] } & Record<string, unknown>> {
  return apiFetch(`/metrics/videoAnalysisData/?uploadId=${uploadId}`);
}

export async function getAnglesTab(
  apiFetch: ApiFetch,
  uploadId: number
): Promise<AnalysisTabViewData> {
  const raw = await apiFetch<unknown>(`/metrics/appAnglesTab`, {
    method: "POST",
    body: JSON.stringify({ id: uploadId }),
  });
  return camelize<AnalysisTabViewData>(raw);
}

export async function getMetricsTab(
  apiFetch: ApiFetch,
  uploadId: number
): Promise<AnalysisTabViewData> {
  const raw = await apiFetch<unknown>(`/metrics/appMetricsTab`, {
    method: "POST",
    body: JSON.stringify({ id: uploadId }),
  });
  return camelize<AnalysisTabViewData>(raw);
}

export async function getFeedbackTab(
  apiFetch: ApiFetch,
  uploadId: number
): Promise<AnalysisTabViewData> {
  const raw = await apiFetch<unknown>(`/metrics/appFeedbackTab`, {
    method: "POST",
    body: JSON.stringify({ id: uploadId }),
  });
  return camelize<AnalysisTabViewData>(raw);
}

// ── Home / dashboard ──────────────────────────────────────────────────────
// PlatformApi.cs:79
export function getDashboardHome(
  apiFetch: ApiFetch
): Promise<DashboardHomeResponse> {
  return apiFetch<DashboardHomeResponse>(`/platform/dashboardHome`);
}

// ── Progress ──────────────────────────────────────────────────────────────
// PlatformApi.cs:273 / :321 — aggregated over the user's last-12-week upload history.
export async function getImproveOverview(
  apiFetch: ApiFetch,
  opts: { activityId?: string } = {}
): Promise<ImproveOverviewResponse> {
  // The same sport-family filter as uploadImproveDetail ("Golf"); none or "all" = every activity.
  const qs = opts.activityId && opts.activityId !== "all" ? `?activityId=${encodeURIComponent(opts.activityId)}` : "";
  const raw = await apiFetch<unknown>(`/platform/uploadImproveOverview${qs}`);
  return camelize<ImproveOverviewResponse>(raw);
}

export async function getImproveDetail(
  apiFetch: ApiFetch,
  opts: { activityId?: number | string; range?: string } = {}
): Promise<ImproveDetailResponse> {
  const params = new URLSearchParams();
  if (opts.activityId != null) params.set("activityId", String(opts.activityId));
  params.set("range", opts.range ?? "12w");
  const raw = await apiFetch<unknown>(
    `/platform/uploadImproveDetail?${params.toString()}`
  );
  return camelize<ImproveDetailResponse>(raw);
}

// ── Reports ───────────────────────────────────────────────────────────────
// MetricsApi.cs:220
export function getReportApp(
  apiFetch: ApiFetch,
  uploadId: number
): Promise<ReportAppResponse> {
  return apiFetch<ReportAppResponse>(`/metrics/videoReportDetailApp`, {
    method: "POST",
    body: JSON.stringify({ id: uploadId }),
  });
}

// ── Settings ──────────────────────────────────────────────────────────────
// PlatformApi.cs:337 / :356
export function getSettings(apiFetch: ApiFetch): Promise<SettingsResponse> {
  return apiFetch<SettingsResponse>(`/platform/settings`);
}

export function updateSettings(
  apiFetch: ApiFetch,
  payload: Record<string, unknown>
): Promise<SettingsResponse> {
  return apiFetch<SettingsResponse>(`/platform/settingsUpdate`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// ── Sport label updates from the analysis screen ─────────────────────────
// Controllers/LabelsController.cs:1845
export function updateSportLabel(
  apiFetch: ApiFetch,
  payload: Record<string, unknown>
): Promise<unknown> {
  return apiFetch(`/labels/updateSportLabel`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// ── Account creation on first login ──────────────────────────────────────
// Controllers/SystemController.cs:68
export function getOrCreateUser(
  apiFetch: ApiFetch,
  payload: { email?: string } & Record<string, unknown>
): Promise<{ id?: number | string } & Record<string, unknown>> {
  return apiFetch(`/system/getOrCreate`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/**
 * Registers the signed-in Auth0 identity with the API, or finds it: POST /system/getOrCreate
 * (SystemController). Auth0's post-login action was meant to do this and does not reach the
 * API, so an account that only exists in Auth0 has no user row — and every call that looks the
 * email up (an upload, for one) answers with nothing. The dashboard calls this once on load.
 * The names may be empty: the server substitutes its defaults, but it dereferences the fields,
 * so they are always sent as strings.
 */
export function ensureAccount(
  apiFetch: ApiFetch,
  user: { email?: string; given_name?: string; family_name?: string }
): Promise<unknown> {
  return apiFetch(`/system/getOrCreate`, {
    method: "POST",
    body: JSON.stringify({
      contactFirstName: user.given_name ?? "",
      contactLastName: user.family_name ?? "",
      emailAddress: user.email ?? "",
      phoneNumber: "",
      password: "",
      contactTitle: 0,
    }),
  });
}
